import React, { useEffect, useState, useRef } from "react";
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Typography,
    Box,
    Divider,
    useMediaQuery,
    FormControlLabel,
    Switch,
    CircularProgress,
    Alert,
    IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useForm, FormProvider, Controller } from "react-hook-form";
import TextInput from "qubCommon/Inputs/TextInput";
import DateInput from "qubCommon/Inputs/DateInput";
import Dropdown from "qubCommon/Inputs/Dropdown";
import FormCheckboxGroup from "qubCommon/Inputs/CheckboxGroup";
import { yupResolver } from "@hookform/resolvers/yup";
import { newUserSchema } from "util/validations/new-user-form";
import FormCheckbox from "qubCommon/Inputs/Checkbox";
import {
    createUser,
    updateUser,
    getUser,
    resendInvitation,
} from "apis/controllers/users";
import { updateUserSchema } from "util/validations/update-user-form";
import { Close } from "@mui/icons-material";
import { useSnackbar } from "notistack";

const getGenderId = (gender) => {
    if (!gender) return "";
    if (typeof gender === "object") {
        return gender.id ?? gender.Id ?? gender.value ?? gender.Value ?? "";
    }
    return gender;
};

const getGenderFormValue = (gender, genderOptions = []) => {
    const genderId = getGenderId(gender);

    if (genderId === "") return "";

    const matchingOption = genderOptions.find(
        (option) => String(getGenderId(option)) === String(genderId)
    );

    return matchingOption ? getGenderId(matchingOption) : genderId;
};

const getUserGenderFormValue = (user, genderOptions = []) =>
    getGenderFormValue(
        user?.gender ?? user?.Gender ?? user?.genderId ?? user?.GenderId,
        genderOptions
    );

const UserDialog = ({
    open,
    onClose,
    roleOptions = [],
    genderOptions = [],
    defaultValues = {},
    loadUsers = () => { },
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isEdit = !!defaultValues?.id;
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(false); // submit loading
    const [loadingUser, setLoadingUser] = useState(false); // getUser loading
    const [message, setMessage] = useState({ text: "", type: "" });
    const [creationSuccess, setCreationSuccess] = useState(false);
    const [employeeSettings, setEmployeeSettings] = useState({});
    const [resending, setResending] = useState(false); // <-- resend loading
    const [deleting, setDeleting] = useState(false);
    const [formVersion, setFormVersion] = useState(0);

    const alertRef = useRef(null);

    // base roles ids from props
    const rolesIdsFromProps =
        defaultValues?.roles?.map((r) => (typeof r === "string" ? r : r.id)) ||
        [];

    const methods = useForm({
        defaultValues: {
            id: defaultValues?.id || "",
            firstName: defaultValues?.firstName || "",
            lastName: defaultValues?.lastName || "",
            userName: defaultValues?.userName || "",
            dob: defaultValues?.dob || "",
            gender: getUserGenderFormValue(defaultValues, genderOptions),
            roles: rolesIdsFromProps,
            isActive:
                typeof defaultValues?.enabled === "boolean"
                    ? defaultValues.enabled
                    : true,
            isGsisPaUser: defaultValues?.isGsisPaUser ?? false,
        },
        resolver: yupResolver(isEdit ? updateUserSchema : newUserSchema),
    });

    const resetWithValues = (values) => {
        methods.reset({
            id: values?.id || "",
            firstName: values?.firstName || "",
            lastName: values?.lastName || "",
            userName: values?.userName || "",
            dob: values?.dob || "",
            gender: getUserGenderFormValue(values, genderOptions),
            roles:
                values?.roles?.map((r) => (typeof r === "string" ? r : r.id)) ||
                [],
            isActive:
                typeof values?.enabled === "boolean"
                    ? values.enabled
                    : true,
            isGsisPaUser: !!values?.isGsisPaUser,
        });
        setFormVersion((currentVersion) => currentVersion + 1);
    };

    useEffect(() => {
        if (!open || !genderOptions?.length) return;

        const currentGender = methods.getValues("gender");
        const normalizedGender = getGenderFormValue(
            currentGender || getUserGenderFormValue(defaultValues, genderOptions),
            genderOptions
        );

        if (normalizedGender !== currentGender) {
            methods.setValue("gender", normalizedGender, {
                shouldDirty: false,
                shouldTouch: false,
                shouldValidate: false,
            });
            setFormVersion((currentVersion) => currentVersion + 1);
        }
    }, [
        defaultValues?.GenderId,
        defaultValues?.gender,
        defaultValues?.genderId,
        genderOptions,
        methods,
        open,
    ]);

    // Όταν ανοίγει το dialog
    useEffect(() => {
        if (!open) return;

        setMessage({ text: "", type: "" });
        setCreationSuccess(false);

        if (isEdit && defaultValues?.id) {
            const fetchUser = async () => {
                try {
                    setLoadingUser(true);
                    const res = await getUser(defaultValues.id);
                    if (res?.status === 'success') {
                        const user = res?.data || {};
                        resetWithValues(user);
                        setEmployeeSettings(res?.data?.employeeSettings || {});
                    }
                    if (res?.status !== 'success') {
                        setMessage({
                            type: 'error',
                            text: res?.message || "Αποτυχία φόρτωσης στοιχείων χρήστη.",
                        })
                    }
                } catch (err) {
                    console.error(err);
                    setMessage({
                        text: "Αποτυχία φόρτωσης στοιχείων χρήστη.",
                        type: "error",
                    });
                    resetWithValues(defaultValues);
                } finally {
                    setLoadingUser(false);
                }
            };

            fetchUser();
        } else {
            // Create mode
            resetWithValues({
                ...defaultValues,
                enabled: true,
                isGsisPaUser: false,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, isEdit, defaultValues?.id]);

    // Scroll στο μήνυμα όταν υπάρχει error
    useEffect(() => {
        if (message.type === "error" && alertRef.current) {
            alertRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }
    }, [message]);

    const handleSubmit = async (data) => {
        if (loading || loadingUser) return;
        setLoading(true);
        setMessage({ text: "", type: "" });

        try {
            let res;
            if (!isEdit) {
                // CREATE
                res = await createUser({
                    firstName: data.firstName?.trim(),
                    lastName: data.lastName?.trim(),
                    userName: data.userName?.trim(),
                    roles: data.roles,
                    isGsisPaUser: !!data.isGsisPaUser,
                });

                if (res?.status === "success") {
                    setMessage({
                        text: "Η δημιουργία του χρήστη ολοκληρώθηκε με επιτυχία!",
                        type: "success",
                    });
                    setCreationSuccess(true);
                } else {
                    setMessage({
                        text: res?.message || "Παρουσιάστηκε σφάλμα.",
                        type: "error",
                    });
                }
            } else {
                // EDIT
                res = await updateUser({
                    id: data.id || defaultValues.id,
                    firstName: data.firstName?.trim(),
                    lastName: data.lastName?.trim(),
                    userName: data.userName?.trim(),
                    roles: data.roles,
                    enabled: data.isActive,
                    action: "save",
                });

                if (res?.status === "success") {
                    enqueueSnackbar("Η ενημέρωση του χρήστη ολοκληρώθηκε με επιτυχία!", {
                        variant: "success",
                    });
                    handleClose(true);
                } else {
                    setMessage({
                        text: res?.message || "Παρουσιάστηκε σφάλμα.",
                        type: "error",
                    });
                }
            }
        } catch (err) {
            console.error(err);
            setMessage({
                text: "Παρουσιάστηκε σφάλμα κατά την αποθήκευση.",
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleResendInvitation = async () => {
        if (resending || loadingUser) return;

        setResending(true);
        try {
            const userId = methods.getValues("id") || defaultValues.id;
            const res = await resendInvitation(userId);

            if (res?.status === "success") {
                enqueueSnackbar("Η πρόσκληση επαναστάλθηκε με επιτυχία.", {
                    variant: "success",
                });
            } else {
                enqueueSnackbar(
                    res?.message ||
                    "Παρουσιάστηκε σφάλμα κατά την επαναποστολή της πρόσκλησης.",
                    { variant: "error" }
                );
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar(
                "Παρουσιάστηκε σφάλμα κατά την επαναποστολή της πρόσκλησης.",
                { variant: "error" }
            );
        } finally {
            setResending(false);
        }
    };

    const handleDeleteUser = async () => {
        if (deleting || loadingUser) return;
        if (window.confirm("Είστε σίγουροι ότι θέλετε να διαγράψετε αυτόν τον χρήστη;")) {

            setDeleting(true);
            try {
                const data = methods.getValues();
                const userId = methods.getValues("id") || defaultValues.id;
                const res = await updateUser({
                    id: userId,
                    firstName: data.firstName?.trim(),
                    lastName: data.lastName?.trim(),
                    userName: data.userName?.trim(),
                    action: "delete",
                });

                if (res?.status === "success") {
                    enqueueSnackbar("Ο χρήστης διαγράφηκε με επιτυχία.", {
                        variant: "success",
                    });
                    // loadUsers();
                    handleClose(true);
                } else {
                    enqueueSnackbar(
                        res?.message ||
                        "Παρουσιάστηκε σφάλμα κατά τη διαγραφή του χρήστη.",
                        { variant: "error" }
                    );
                }
            } catch (err) {
                console.error(err);
                enqueueSnackbar(
                    "Παρουσιάστηκε σφάλμα κατά την επαναποστολή της πρόσκλησης.",
                    { variant: "error" }
                );
            } finally {
                setDeleting(false);
            }
        }
    };

    const handleClose = async (shouldReload = false) => {
        shouldReload && loadUsers(); // Reload users list if created successfully
        // Reset local component states
        setMessage({ text: "", type: "" });
        setCreationSuccess(false);
        setEmployeeSettings({});

        setLoading(false);
        setLoadingUser(false);
        setResending(false);
        setDeleting(false);

        // Reset form values to initial defaults
        methods.reset({
            id: defaultValues?.id || "",
            firstName: defaultValues?.firstName || "",
            lastName: defaultValues?.lastName || "",
            userName: defaultValues?.userName || "",
            dob: defaultValues?.dob || "",
            gender: getUserGenderFormValue(defaultValues, genderOptions),
            roles:
                defaultValues?.roles?.map((r) =>
                    typeof r === "string" ? r : r.id
                ) || [],
            isActive:
                typeof defaultValues?.enabled === "boolean"
                    ? defaultValues.enabled
                    : true,
            isGsisPaUser: false,
        });

        // Finally close the dialog
        onClose();
    };


    const submittingOrLoading = loading || loadingUser;

    return (
        <Dialog
            open={open}
            onClose={() => handleClose(creationSuccess)}
            fullWidth
            maxWidth="md"
            fullScreen={isMobile}
            PaperProps={{
                sx: { borderRadius: isMobile ? 0 : 3, overflow: "hidden" },
            }}
        >
            {/* Header */}
            <Box sx={{ backgroundColor: "#f5f8fa", px: 3, py: 2 }}>
                <Typography variant="h4" fontWeight={700} color="primary">
                    {isEdit ? "Στοιχεία Χρήστη" : "Νέος Χρήστης"}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => handleClose(creationSuccess)}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <Close />
                </IconButton>
                <Typography variant="body2" color="text.secondary">
                    {isEdit
                        ? "Τροποποιήστε τα στοιχεία του χρήστη."
                        : "Συμπληρώστε τα στοιχεία του χρήστη."}
                </Typography>
            </Box>

            <Divider />

            <FormProvider {...methods}>
                <DialogContent sx={{ px: 3, py: 3, backgroundColor: "#fff" }}>
                    <Grid container spacing={2}>
                        {loadingUser && (
                            <Grid item xs={12} sx={{ textAlign: "center", py: 4 }}>
                                <CircularProgress />
                            </Grid>
                        )}

                        {!loadingUser && !(!isEdit && creationSuccess) && (
                            <>
                                <Grid item xs={12} sm={6}>
                                    <TextInput
                                        name="firstName"
                                        label="Όνομα"
                                        isFullWidth
                                        disabled={
                                            isEdit &&
                                            !employeeSettings?.userSettings?.canEdit
                                        }
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextInput
                                        name="lastName"
                                        label="Επώνυμο"
                                        isFullWidth
                                        disabled={
                                            isEdit &&
                                            !employeeSettings?.userSettings?.canEdit
                                        }
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextInput
                                        name="userName"
                                        label="Email"
                                        isFullWidth
                                        disabled={
                                            isEdit &&
                                            !employeeSettings?.userSettings?.canEditEmail
                                        }
                                    />
                                </Grid>



                                <Grid item xs={12} sm={6}>
                                    {genderOptions?.length > 0 && (
                                        <Dropdown
                                            key={`gender-${formVersion}-${genderOptions.length}`}
                                            name="gender"
                                            label="Φύλο"
                                            isFullWidth
                                            disabled={
                                                isEdit &&
                                                !employeeSettings?.userSettings?.canEdit
                                            }
                                            valueName="id"
                                            labelName="name"
                                            options={genderOptions}
                                            defaultValue={
                                                methods.getValues("gender") ||
                                                getUserGenderFormValue(defaultValues, genderOptions)
                                            }
                                        />
                                    )}
                                </Grid>

                                {isEdit && (
                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        display="flex"
                                        alignItems="flex-end"
                                    >
                                        <Controller
                                            name="isActive"
                                            control={methods.control}
                                            render={({ field }) => (
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            disabled={
                                                                isEdit &&
                                                                !employeeSettings
                                                                    ?.userSettings
                                                                    ?.canEnableDisable
                                                            }
                                                            color="primary"
                                                            checked={!!field.value}
                                                            onChange={(e) =>
                                                                field.onChange(
                                                                    e.target.checked
                                                                )
                                                            }
                                                            sx={{
                                                                "& .MuiSwitch-track":
                                                                {
                                                                    backgroundColor:
                                                                        field.value
                                                                            ? "success.main"
                                                                            : "#bdbdbd",
                                                                },
                                                                "& .MuiSwitch-thumb":
                                                                {
                                                                    backgroundColor:
                                                                        "#fff",
                                                                },
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        field.value
                                                            ? "Ενεργός"
                                                            : "Ανενεργός"
                                                    }
                                                />
                                            )}
                                        />
                                    </Grid>
                                )}

                                <Grid
                                    container
                                    item
                                    xs={12}
                                    sm={6}
                                >
                                    <FormCheckbox
                                        name="isGsisPaUser"
                                        label="Διαθέτει κωδικούς Δημόσιας Διοίκησης"
                                        defaultValue={defaultValues?.isGsisPaUser ?? false}
                                        disabled={
                                            isEdit &&
                                            !employeeSettings?.userSettings?.canEdit
                                        }
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FormCheckboxGroup
                                        disabled={isEdit &&
                                            !employeeSettings?.roleSettings?.canEdit
                                        }
                                        name="roles"
                                        vertical={true}
                                        options={roleOptions}
                                        valueName="id"
                                        labelName="name"
                                        groupLabel="Ρόλοι Χρήστη"
                                    />
                                </Grid>
                            </>
                        )}

                        {message.text && (
                            <Grid item xs={12}>
                                <Alert
                                    ref={alertRef}
                                    severity={
                                        message.type === "success"
                                            ? "success"
                                            : "error"
                                    }
                                >
                                    {message.text}
                                </Alert>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>

                <Divider />

                <DialogActions
                    sx={{
                        backgroundColor: "#f9f9f9",
                        py: 2,
                        px: 3,
                        display: "flex",
                        gap: 1.5,
                        justifyContent: "flex-end",
                        flexWrap: "wrap",
                    }}
                >
                    {/* LEFT GROUP */}
                    <Box
                        sx={{
                            display: "flex",
                            gap: 1,
                            flexWrap: "wrap",
                            width: { xs: "100%", sm: "auto" },
                        }}
                    >
                        <Button
                            sx={{ fontWeight: 600 }}
                            onClick={() => handleClose(creationSuccess)}
                            variant="outlined"
                            color="primary"
                            disabled={submittingOrLoading}
                            fullWidth={isMobile}
                        >
                            {creationSuccess ? "Κλείσιμο" : "Ακύρωση"}
                        </Button>

                        {isEdit &&
                            employeeSettings?.userSettings?.canDelete && (
                                <Button
                                    sx={{ fontWeight: 600 }}
                                    variant="contained"
                                    color="error"
                                    onClick={() =>
                                        handleDeleteUser()
                                    }
                                    disabled={deleting || submittingOrLoading}
                                    startIcon={
                                        deleting && (
                                            <CircularProgress size={20} />
                                        )
                                    }
                                    fullWidth={isMobile}
                                >
                                    Διαγραφή Χρήστη
                                </Button>
                            )}
                    </Box>

                    <Box sx={{ flexGrow: 1 }} />

                    {/* RIGHT GROUP */}
                    <Box
                        sx={{
                            display: "flex",
                            gap: 1,
                            flexWrap: "wrap",
                            width: { xs: "100%", sm: "auto" },
                        }}
                    >
                        {isEdit &&
                            employeeSettings?.userSettings?.canResendInvite && (
                                <Button
                                    variant="contained"
                                    sx={{
                                        fontWeight: 600,
                                        backgroundColor: "warning.dark",
                                        color: "#fff",
                                        "&:hover": {
                                            backgroundColor:
                                                "rgba(255, 136, 1, 0.93)",
                                        },
                                    }}
                                    onClick={handleResendInvitation}
                                    disabled={resending || submittingOrLoading}
                                    startIcon={
                                        resending && (
                                            <CircularProgress size={20} />
                                        )
                                    }
                                    fullWidth={isMobile}
                                >
                                    Επαναποστολή Πρόσκλησης
                                </Button>
                            )}

                        <Button
                            sx={{ fontWeight: 600, px: 3 }}
                            variant="contained"
                            color="primary"
                            onClick={methods.handleSubmit(handleSubmit)}
                            disabled={
                                submittingOrLoading ||
                                (!isEdit && creationSuccess)
                            }
                            startIcon={
                                submittingOrLoading && (
                                    <CircularProgress size={20} />
                                )
                            }
                            fullWidth={isMobile}
                        >
                            {isEdit
                                ? "Αποθήκευση Αλλαγών"
                                : "Δημιουργία Χρήστη"}
                        </Button>
                    </Box>
                </DialogActions>
            </FormProvider>
        </Dialog>
    );
};

export default UserDialog;
